import { useState, useMemo, useEffect } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment, Alert, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, MenuItem, Stack, Skeleton,
  Select, OutlinedInput, Checkbox, ListItemText, FormControl, InputLabel,
} from '@mui/material'
import { Speed, Search, WarningAmber } from '@mui/icons-material'
import { useApi } from '../../hooks/useApi'
import ExportMenu from '../../components/common/ExportMenu'

// FAR-42 Item Run Rates. Read-only view of the Run Rate Table, Faribault (1002)
// only. Shows the full calculation chain so planners can see how each rate is
// built. Values are shown exactly as loaded; rows where CPM x Efficiency does
// not equal CPM After Efficiency are flagged, not corrected.

const DASH = '—'

// Left to right exactly as FAR-42 §3 specifies.
const COLUMNS = [
  { id: 'productionLine', label: 'Line' },
  { id: 'materialNumber', label: 'Item #' },
  { id: 'briteCode', label: 'Brite' }, // plain text, not a quantity
  { id: 'description', label: 'Description' },
  { id: 'packSize', label: 'Pack Size', numeric: true },
  { id: 'cpm', label: 'CPM', numeric: true },
  { id: 'efficiency', label: 'Efficiency', numeric: true },
  { id: 'cpmAfterEfficiency', label: 'CPM After Efficiency', numeric: true },
  { id: 'runRateCansPerHr', label: 'Run Rate Cans/Hr', numeric: true },
  { id: 'runRateCasesPerHr', label: 'Run Rate CS/Hr', numeric: true },
]

const pct = (v) => (typeof v === 'number' ? `${Math.round(v * 1000) / 10}%` : DASH)
// Show the file's value exactly: 227.5 stays 227.5, 560 stays 560.
const numFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
const num = (v) => (typeof v === 'number' ? numFmt.format(v) : DASH)

export default function ItemRunRates() {
  const { get, loading, error } = useApi()
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ importedAt: '', sourceFile: '', cpmMismatches: 0 })
  const [itemSearch, setItemSearch] = useState('')
  const [briteSearch, setBriteSearch] = useState('')
  const [descSearch, setDescSearch] = useState('')
  const [lines, setLines] = useState([])
  const [orderBy, setOrderBy] = useState('materialNumber')
  const [order, setOrder] = useState('asc')

  useEffect(() => {
    let active = true
    get('/run-rates?plant=1002')
      .then((res) => {
        if (!active || !res) return
        setRows(res.runRates || [])
        setMeta({ importedAt: res.importedAt || '', sourceFile: res.sourceFile || '', cpmMismatches: res.cpmMismatches || 0 })
      })
      .catch(() => { /* surfaced via useApi().error */ })
    return () => { active = false }
  }, [get])

  const lineOptions = useMemo(
    () => [...new Set(rows.map((r) => r.productionLine).filter(Boolean))].sort(),
    [rows],
  )

  const filtered = useMemo(() => {
    const itemQ = itemSearch.trim().toLowerCase()
    const briteQ = briteSearch.trim().toLowerCase()
    const descQ = descSearch.trim().toLowerCase()
    // AND logic across all four filters (FAR-42 §4).
    const out = rows.filter((r) => {
      if (lines.length && !lines.includes(r.productionLine)) return false
      if (itemQ && !String(r.materialNumber).toLowerCase().includes(itemQ)) return false
      if (briteQ && !String(r.briteCode ?? '').toLowerCase().includes(briteQ)) return false
      if (descQ && !String(r.description ?? '').toLowerCase().includes(descQ)) return false
      return true
    })
    return [...out].sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      let cmp
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv
      else if (av == null && bv != null) cmp = 1 // blanks last
      else if (bv == null && av != null) cmp = -1
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true })
      return order === 'asc' ? cmp : -cmp
    })
  }, [rows, itemSearch, briteSearch, descSearch, lines, orderBy, order])

  const handleSort = (id) => {
    if (orderBy === id) setOrder(order === 'asc' ? 'desc' : 'asc')
    else { setOrderBy(id); setOrder('asc') }
  }

  const importedLabel = meta.importedAt
    ? new Date(meta.importedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null

  const renderCell = (row, col) => {
    const v = row[col.id]
    if (col.id === 'efficiency') return pct(v)
    if (col.id === 'cpmAfterEfficiency' && row.cpmMismatch) {
      return (
        <Tooltip title={`CPM ${num(row.cpm)} × ${pct(row.efficiency)} = ${num(Math.round(row.cpm * row.efficiency * 100) / 100)}, but the file says ${num(v)}. Shown as given.`}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}>
            <WarningAmber sx={{ fontSize: 16 }} />{num(v)}
          </Box>
        </Tooltip>
      )
    }
    if (col.numeric) return num(v)
    return v || DASH
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Speed color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Item Run Rates</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Faribault (1002)" size="small" variant="outlined" />
        {importedLabel && (
          <Typography variant="overline" color="text.secondary">
            Loaded {importedLabel}{meta.sourceFile ? ` · ${meta.sourceFile}` : ''} · {rows.length} items
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {meta.cpmMismatches > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          In {meta.cpmMismatches} rows, CPM × Efficiency doesn't equal CPM After Efficiency. Values are shown as
          given in the source file and flagged <WarningAmber sx={{ fontSize: 14, verticalAlign: 'middle' }} />.
          Run Rate CS/Hr follows from CPM After Efficiency.
        </Alert>
      )}

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Item #" value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} sx={{ width: 180 }} />
        <TextField size="small" label="Brite" value={briteSearch} onChange={(e) => setBriteSearch(e.target.value)} sx={{ width: 140 }} />
        <TextField
          size="small" label="Description" value={descSearch} onChange={(e) => setDescSearch(e.target.value)} sx={{ width: 280 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
        />
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel id="line-filter">Line</InputLabel>
          <Select
            labelId="line-filter" multiple value={lines}
            onChange={(e) => setLines(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="Line" />}
            renderValue={(sel) => (sel.length ? sel.join(', ') : 'All lines')}
          >
            {lineOptions.map((l) => (
              <MenuItem key={l} value={l}>
                <Checkbox size="small" checked={lines.includes(l)} />
                <ListItemText primary={l} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {filtered.length} of {rows.length}
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.numeric ? 'right' : 'left'} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleSort(col.id)}>
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>{COLUMNS.map((c) => <TableCell key={c.id}><Skeleton variant="text" /></TableCell>)}</TableRow>
                ))
              : filtered.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {rows.length === 0 ? 'No run rates loaded yet.' : 'No items match the current filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                : filtered.map((row) => (
                    <TableRow key={row.materialNumber} hover>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id} align={col.numeric ? 'right' : 'left'} sx={col.id === 'materialNumber' ? { fontWeight: 600 } : undefined}>
                          {renderCell(row, col)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
